import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import Breadcrumb from "./Breadcrumb";
import { useDataContext } from "./DataContext";
import firebaseApp from "./Firecontact";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { Helmet } from "react-helmet-async";
import Modal from "react-bootstrap/Modal";
import {
  FaWhatsapp,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaDownload,
  FaShareAlt,
  FaCheckCircle,
  FaShippingFast,
  FaStar,
} from "react-icons/fa";

/* ---------------- AMAZON STYLE HOVER ZOOM (Desktop only) ---------------- */
const AmazonZoom = ({ src, alt, onOpenModal }) => {
  const [isHovering, setIsHovering] = useState(false);
  const [lensPosition, setLensPosition] = useState({ x: 0, y: 0 });
  const [backgroundPosition, setBackgroundPosition] = useState("0% 0%");
  const containerRef = useRef(null);

  const LENS_SIZE = 150;
  const ZOOM_LEVEL = 2.5;

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 992);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const handleMouseMove = (e) => {
    if (isMobile || !containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    const lensX = Math.max(
      LENS_SIZE / 2,
      Math.min(x, rect.width - LENS_SIZE / 2),
    );
    const lensY = Math.max(
      LENS_SIZE / 2,
      Math.min(y, rect.height - LENS_SIZE / 2),
    );

    setLensPosition({ x: lensX - LENS_SIZE / 2, y: lensY - LENS_SIZE / 2 });

    const bgX = (x / rect.width) * 100;
    const bgY = (y / rect.height) * 100;
    setBackgroundPosition(`${bgX}% ${bgY}%`);
  };

  const handleMouseEnter = () => {
    if (!isMobile) setIsHovering(true);
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const handleClick = () => {
    if (onOpenModal) onOpenModal();
  };

  return (
    <div
      className="amazon-zoom-wrapper"
      style={{ position: "relative", width: "100%" }}
    >
      <div
        ref={containerRef}
        className="amazon-zoom-main"
        onMouseMove={handleMouseMove}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        style={{
          cursor: isMobile ? "zoom-in" : "crosshair",
          position: "relative",
          overflow: "hidden",
          borderRadius: "0px",
          backgroundColor: "#f8f9fa",
        }}
      >
        <img
          src={src}
          alt={alt}
          className="amazon-zoom-img"
          draggable={false}
          style={{
            width: "100%",
            height: "auto",
            objectFit: "contain",
          }}
        />

        {/* Lens overlay - Desktop only */}
        {isHovering && !isMobile && (
          <div
            className="amazon-zoom-lens"
            style={{
              position: "absolute",
              left: lensPosition.x,
              top: lensPosition.y,
              width: LENS_SIZE,
              height: LENS_SIZE,
              border: "1px solid #3085de",
              backgroundColor: "rgba(86, 125, 224, 0.2)",
              pointerEvents: "none",
              zIndex: 10,
            }}
          />
        )}

        {/* Zoom icon hint */}
        <div
          className="amazon-zoom-hint d-flex"
          style={{
            position: "absolute",
            bottom: "10px",
            right: "10px",
            background: "rgba(0,0,0,0.6)",
            color: "white",
            padding: "8px",
            borderRadius: "50%",
            cursor: "pointer",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            <line x1="11" y1="8" x2="11" y2="14"></line>
            <line x1="8" y1="11" x2="14" y2="11"></line>
          </svg>
        </div>
      </div>

      {/* Zoom Preview - Desktop only */}
      {isHovering && !isMobile && (
        <div
          className="amazon-zoom-preview"
          style={{
            position: "absolute",
            top: 0,
            left: "calc(100% + 20px)",
            width: "400px",
            height: "400px",
            backgroundImage: `url(${src})`,
            backgroundSize: `${ZOOM_LEVEL * 100}%`,
            backgroundPosition: backgroundPosition,
            border: "2px solid #ddd",
            borderRadius: "12px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            zIndex: 1000,
            backgroundColor: "#fff",
          }}
        />
      )}
    </div>
  );
};

/* ---------------- SKELETON ---------------- */
const ProductDetailsSkeleton = () => (
  <div className="container py-4">
    <div className="row g-4">
      <div className="col-lg-6">
        <div className="d-flex gap-3">
          {/* Vertical Thumbnails - Left Side (Desktop) */}
          <div
            className="d-none d-lg-flex flex-column gap-2"
            style={{ width: "80px" }}
          >
            {[1, 2, 3, 4].map((i) => (
              <div
                key={i}
                className="bg-light rounded"
                style={{ width: "80px", height: "80px" }}
              />
            ))}
          </div>
          {/* Main Image */}
          <div className="flex-grow-1">
            <div className="bg-light rounded" style={{ height: "400px" }} />
          </div>
        </div>
        {/* Mobile Dots */}
        <div className="d-lg-none d-flex justify-content-center gap-2 mt-3">
          {[1, 2, 3, 4].map((i) => (
            <div
              key={i}
              className="bg-light rounded-circle"
              style={{ width: "10px", height: "10px" }}
            />
          ))}
        </div>
      </div>
      <div className="col-lg-6">
        <div className="placeholder-glow">
          <span className="placeholder col-8 mb-3" style={{ height: "32px" }} />
          <span className="placeholder col-12 mb-2" />
          <span className="placeholder col-12 mb-3" />
          <span className="placeholder col-12 mb-2" />
          <span className="placeholder col-12 mb-2" />
          <span className="placeholder col-10 mb-4" />
          <span className="placeholder col-5" style={{ height: "48px" }} />
        </div>
      </div>
    </div>
  </div>
);

/* ---------------- NEW ZOOMABLE IMAGE COMPONENT (FROM USER REQUEST) ---------------- */
const ZoomableImage = ({ src, alt }) => {
  const [isZoomed, setIsZoomed] = useState(false);
  const [position, setPosition] = useState({ x: "50%", y: "50%" });

  const handleMouseMove = (e) => {
    if (!isZoomed) return;
    const { left, top, width, height } =
      e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - left) / width) * 100;
    const y = ((e.clientY - top) / height) * 100;
    setPosition({ x: `${x}%`, y: `${y}%` });
  };

  const handleClick = (e) => {
    e.stopPropagation();
    setIsZoomed(!isZoomed);
    if (isZoomed) {
      setPosition({ x: "50%", y: "50%" });
    }
  };

  return (
    <div
      className="zoom-container"
      style={{
        overflow: "hidden",
        cursor: isZoomed ? "zoom-out" : "zoom-in",
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
    >
      <img
        src={src}
        alt={alt}
        style={{
          transform: isZoomed ? "scale(2.5)" : "scale(1)",
          transformOrigin: `${position.x} ${position.y}`,
          transition: "transform 0.05s ease-out", // Faster transition for smoother follow
          width: "100%",
          height: "100%",
          maxHeight: "90vh",
          objectFit: "contain",
          pointerEvents: "none", // Let wrapper handle events
        }}
        draggable={false}
      />
    </div>
  );
};

const ProductDetails = () => {
  const { id } = useParams();
  const {
    products: cachedProducts,
    fetchProducts,
    fetchAllProducts,
    getProductById,
  } = useDataContext();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [modalMainIdx, setModalMainIdx] = useState(0);
  const [allProducts, setAllProducts] = useState([]);
  const [imageIndices, setImageIndices] = useState({});
  const [moreProductsLoading, setMoreProductsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setActiveImageIndex(0);
      try {
        // Try to get from cache first
        const cachedProduct = await getProductById(id);
        if (cachedProduct) {
          setProduct(cachedProduct);
        } else {
          // Fallback to direct fetch if not in cache
          const db = firebaseApp.firestore();
          const doc = await db.collection("product").doc(id).get();
          if (doc.exists) {
            setProduct({ id: doc.id, ...doc.data() });
          } else {
            toast.error("Product not found!");
          }
        }
      } catch (error) {
        console.error("Error fetching product:", error);
        toast.error("Failed to load product details");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchProduct();
    }
  }, [id, getProductById]);

  /* Fetch More Products from cache or API */
  useEffect(() => {
    const loadMoreProducts = async () => {
      setMoreProductsLoading(true);
      try {
        // Use cached products or fetch
        let products = cachedProducts;
        if (!products || products.length === 0 || products.length < 10) {
          // Fetch all if we don't have enough or want to ensure we have ALL
          products = await fetchAllProducts();
        }
        if (products) {
          setAllProducts(products);
          // Initialize image indices for auto-slider
          const indices = {};
          products.forEach((p) => (indices[p.id] = 0));
          setImageIndices(indices);
        }
      } catch (error) {
        console.error("Error fetching all products:", error);
      } finally {
        setMoreProductsLoading(false);
      }
    };

    // Only fetch more products after main product has loaded
    if (!loading && product) {
      loadMoreProducts();
    }
  }, [loading, product, cachedProducts, fetchProducts, fetchAllProducts]);

  /* ---------------- WHATSAPP INQUIRY ---------------- */
  const handleWhatsAppInquiry = () => {
    const text = `Hello, I am interested in *${product?.productName}*.\nLink: ${window.location.href}`;
    const url = `https://wa.me/919925615191?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  /* ---------------- SHARE ---------------- */
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product?.productName,
          text: `Check out ${product?.productName} on 9R NFC!`,
          url: window.location.href,
        });
      } catch (error) {
        console.log("Error sharing", error);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  /* ---------------- IMAGE NAVIGATION ---------------- */
  const images = product?.images || [product?.image];
  const mainSrc = images.length > 0 ? images[activeImageIndex] : null;

  const handlePrevImage = (e) => {
    if (e) e.stopPropagation();
    setActiveImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const handleNextImage = (e) => {
    if (e) e.stopPropagation();
    setActiveImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  /* ---------------- THUMBNAIL LOGIC ---------------- */
  const MAX_THUMBS = 4;
  const showMoreOverlay = images.length > MAX_THUMBS;
  const displayedThumbs = images.slice(0, MAX_THUMBS);
  const remainingCount = images.length - MAX_THUMBS + 1;

  const handleThumbClick = (index) => {
    if (showMoreOverlay && index === MAX_THUMBS - 1) {
      setModalMainIdx(0);
      setShowGalleryModal(true);
    } else {
      setActiveImageIndex(index);
    }
  };

  const openGalleryModal = (startIdx = 0) => {
    setModalMainIdx(startIdx);
    setShowGalleryModal(true);
  };

  const nextModalImage = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      setModalMainIdx((prev) => (prev + 1) % images.length);
    },
    [images.length],
  );

  const prevModalImage = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      setModalMainIdx((prev) => (prev - 1 + images.length) % images.length);
    },
    [images.length],
  );

  /* ---------------- PRICE LOGIC ---------------- */
  const price = Number(product?.price) || 0;
  const offerPercent = Number(product?.offerPrice) || 0;
  const hasDiscount = offerPercent > 0;
  const finalPrice =
    product?.finalPrice ||
    (hasDiscount ? Math.round(price * (1 - offerPercent / 100)) : price);

  /* ---------------- KEYBOARD NAVIGATION FOR MODAL ---------------- */
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!showGalleryModal) return;
      if (e.key === "ArrowLeft") prevModalImage();
      if (e.key === "ArrowRight") nextModalImage();
      if (e.key === "Escape") setShowGalleryModal(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showGalleryModal, nextModalImage, prevModalImage]);

  return (
    <>
      <Helmet>
        <title>{product?.productName || "Product Details"} | 9R NFC Card</title>
        {product && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Product",
              "name": product.productName,
              "image": product.images || [product.image],
              "description": product.description || `Buy ${product.productName} on 9R NFC. Premium smart NFC card for instant contactless sharing.`,
              "sku": product.sku || product.id,
              "brand": {
                "@type": "Brand",
                "name": product.brand || "9R NFC"
              },
              "offers": {
                "@type": "Offer",
                "url": typeof window !== "undefined" ? window.location.href : `https://9rnfc.com/product/${id}`,
                "priceCurrency": "INR",
                "price": finalPrice,
                "itemCondition": "https://schema.org/NewCondition",
                "availability": "https://schema.org/InStock"
              }
            })}
          </script>
        )}
      </Helmet>

      <div className="section-background">
        <Header name="Products" />

        {/* Breadcrumb */}
        <Breadcrumb
          title="Product"
          highlight="Details"
          paths={[
            { name: "Home", link: "/Home" },
            { name: "Products", link: "/Products" },
            { name: product?.productName || "Details", link: null },
          ]}
        />

        {/* Product Details Content */}
        <section className="py-5 bg-white">
          {loading ? (
            <ProductDetailsSkeleton />
          ) : product ? (
            <div className="container">
              <div className="row g-4">
                {/* Left Column - Images */}
                <div className="col-lg-6 sticky-sidebar">
                  <div className="d-flex gap-3">
                    {/* Vertical Thumbnails - Desktop */}
                    {images.length > 1 && (
                      <div
                        className="d-none d-lg-flex flex-column gap-2"
                        style={{ width: "80px" }}
                      >
                        {displayedThumbs.map((src, i) => {
                          const isOverlayThumb =
                            showMoreOverlay && i === MAX_THUMBS - 1;
                          return (
                            <div
                              key={i}
                              className={`border rounded overflow-hidden position-relative ${i === activeImageIndex && !isOverlayThumb ? "border-primary border-2" : ""}`}
                              style={{
                                width: "80px",
                                height: "80px",
                                cursor: "pointer",
                              }}
                              onClick={() => handleThumbClick(i)}
                            >
                              <img
                                src={src}
                                alt={`View ${i + 1}`}
                                className="w-100 h-100"
                                style={{
                                  objectFit: "cover",
                                  pointerEvents: "none",
                                }}
                              />
                              {isOverlayThumb && (
                                <div
                                  className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
                                  style={{
                                    background: "rgba(0,0,0,0.6)",
                                    color: "white",
                                    fontWeight: "bold",
                                  }}
                                >
                                  +{remainingCount}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Main Image with Amazon Zoom */}
                    <div className="flex-grow-1 position-relative">
                      {mainSrc ? (
                        <AmazonZoom
                          key={mainSrc}
                          src={mainSrc}
                          alt={product.productName}
                          onOpenModal={() => openGalleryModal(activeImageIndex)}
                        />
                      ) : (
                        <div
                          className="bg-light rounded d-flex align-items-center justify-content-center"
                          style={{ height: "400px" }}
                        >
                          <span className="text-muted">No Image Available</span>
                        </div>
                      )}

                      {/* Mobile Slider Controls */}
                      {images.length > 1 && (
                        <>
                          <button
                            className="btn btn-light position-absolute d-lg-none shadow-sm d-flex justify-content-center align-items-center"
                            style={{
                              top: "50%",
                              left: "10px",
                              transform: "translateY(-50%)",
                              borderRadius: "50%",
                              width: "40px",
                              height: "40px",
                              padding: 0,
                            }}
                            onClick={handlePrevImage}
                          >
                            <FaChevronLeft />
                          </button>
                          <button
                            className="btn btn-light position-absolute d-lg-none shadow-sm d-flex justify-content-center align-items-center"
                            style={{
                              top: "50%",
                              right: "10px",
                              transform: "translateY(-50%)",
                              borderRadius: "50%",
                              width: "40px",
                              height: "40px",
                              padding: 0,
                            }}
                            onClick={handleNextImage}
                          >
                            <FaChevronRight />
                          </button>

                          {/* Mobile Dots */}
                          <div className="d-lg-none d-flex justify-content-center gap-2 mt-3">
                            {images.map((_, i) => (
                              <span
                                key={i}
                                className={`rounded-circle ${i === activeImageIndex ? "bg-primary" : "bg-secondary"}`}
                                style={{
                                  width: "10px",
                                  height: "10px",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                }}
                                onClick={() => setActiveImageIndex(i)}
                              />
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Column - Details */}
                <div className="col-lg-6">
                  {/* Category & Share */}
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    {product.category && (
                      <span className="badge bg-secondary">
                        {product.category}
                      </span>
                    )}
                    <button
                      className="btn btn-link p-0"
                      onClick={handleShare}
                      title="Share"
                      style={{ color: "#555", fontSize: "1.2rem" }}
                    >
                      <FaShareAlt />
                    </button>
                  </div>

                  <h2 className="fw-bold mb-3">{product.productName}</h2>

                  {/* Pricing */}
                  <div className="mb-3 p-3 bg-light rounded">
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <span className="fw-bold text-dark fs-3">
                        ₹{finalPrice}
                      </span>
                      {hasDiscount && (
                        <>
                          <span className="text-danger text-decoration-line-through fs-5">
                            ₹{price}
                          </span>
                          <span className="badge bg-success fs-6">
                            {offerPercent}% OFF
                          </span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Description */}
                  {/* Description */}
                  {product.description && (
                    <div className="mb-4">
                      <h5 className="fw-bold mb-3 border-bottom pb-2">
                        Description
                      </h5>
                      <p
                        className="text-muted lh-base"
                        style={{ whiteSpace: "pre-line", fontSize: "1rem" }}
                      >
                        {product.description}
                      </p>
                    </div>
                  )}

                  {/* Features - Grid Layout */}
                  {product.features && product.features.length > 0 && (
                    <div className="mb-4">
                      <h5 className="fw-bold mb-3 border-bottom pb-2">
                        Key Features
                      </h5>
                      <div className="row g-3">
                        {product.features.map((feature, idx) => (
                          <div key={idx} className="col-md-6">
                            <div className="d-flex align-items-center p-3 border rounded bg-light bg-opacity-25 h-100">
                              <FaCheckCircle
                                className="text-success me-3 flex-shrink-0"
                                size={18}
                              />
                              <span className="fw-500 text-dark">
                                {feature}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Specifications Table - Modern */}
                  <div className="mb-4">
                    <h5 className="fw-bold mb-3 border-bottom pb-2">
                      Product Details
                    </h5>
                    <div className="table-responsive rounded border">
                      <table className="table table-striped table-hover mb-0">
                        <tbody>
                          {product.sku && (
                            <tr>
                              <td
                                className="fw-600 text-secondary ps-4"
                                style={{ width: "35%" }}
                              >
                                SKU
                              </td>
                              <td className="fw-500">{product.sku}</td>
                            </tr>
                          )}
                          {product.brand && (
                            <tr>
                              <td className="fw-600 text-secondary ps-4">
                                Brand
                              </td>
                              <td className="fw-500">{product.brand}</td>
                            </tr>
                          )}
                          {product.category && (
                            <tr>
                              <td className="fw-600 text-secondary ps-4">
                                Category
                              </td>
                              <td className="fw-500">{product.category}</td>
                            </tr>
                          )}
                          {/* Dynamic Specifications */}
                          {product.specifications &&
                            product.specifications.map((spec, idx) => (
                              <tr key={idx}>
                                <td className="fw-600 text-secondary ps-4">
                                  {spec.key}
                                </td>
                                <td className="fw-500">{spec.value}</td>
                              </tr>
                            ))}
                          {/* Fallback for Material/Size */}
                          {!product.specifications?.some(
                            (s) => s.key.toLowerCase() === "material",
                          ) &&
                            product.material && (
                              <tr>
                                <td className="fw-600 text-secondary ps-4">
                                  Material
                                </td>
                                <td className="fw-500">{product.material}</td>
                              </tr>
                            )}
                          {!product.specifications?.some(
                            (s) => s.key.toLowerCase() === "size",
                          ) &&
                            product.size && (
                              <tr>
                                <td className="fw-600 text-secondary ps-4">
                                  Size
                                </td>
                                <td className="fw-500">{product.size}</td>
                              </tr>
                            )}
                          {product.tags && product.tags.length > 0 && (
                            <tr>
                              <td className="fw-600 text-secondary ps-4">
                                Tags
                              </td>
                              <td>
                                <div className="d-flex flex-wrap gap-1">
                                  {Array.isArray(product.tags)
                                    ? product.tags.map((tag, index) => (
                                        <span
                                          key={index}
                                          className="badge bg-light text-dark border"
                                        >
                                          {tag}
                                        </span>
                                      ))
                                    : product.tags}
                                </div>
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* How It Works - Steps */}
                  {product.howItWorks && product.howItWorks.length > 0 && (
                    <div className="mb-4">
                      <h5 className="fw-bold mb-4 border-bottom pb-2">
                        How It Works
                      </h5>
                      <div className="">
                        {product.howItWorks.map((step, idx) => (
                          <div key={idx} className="d-flex mb-3">
                            <div className="flex-shrink-0 me-3 d-flex align-items-center">
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center text-white shadow"
                                style={{
                                  width: "40px",
                                  height: "40px",
                                  backgroundColor: "#333",
                                  fontSize: "1.1rem",
                                  fontWeight: "bold",
                                }}
                              >
                                {idx + 1}
                              </div>
                            </div>
                            <div className="bg-white p-3 border rounded shadow-sm w-100">
                              {/* Arrow decoration (optional, removed for cleaner look) */}
                              <p className="mb-0 text-dark fw-500">{step}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Why Us & Delivery Grid */}
                  <div className="row g-4 mb-4">
                    {product.whyUs && (
                      <div className="col-md-12">
                        <div className="why-us-card p-4 h-100 rounded bg-white shadow-sm border">
                          <div className="d-flex align-items-center mb-3">
                            <div className="p-2 bg-warning bg-opacity-10 rounded-circle me-3">
                              <FaStar className="text-warning" size={24} />
                            </div>
                            <h6 className="fw-bold mb-0 fs-5">
                              Why Choose Us?
                            </h6>
                          </div>
                          <p className="text-muted mb-0 lh-base">
                            {product.whyUs}
                          </p>
                        </div>
                      </div>
                    )}
                    {product.deliveryInfo && (
                      <div className="col-md-12">
                        <div className="p-4 h-100 rounded border bg-white shadow-sm overflow-hidden">
                          <div className="d-flex align-items-center mb-3">
                            <div className="p-2 bg-info bg-opacity-10 rounded-circle me-3">
                              <FaShippingFast className="text-info" size={24} />
                            </div>
                            <h6 className="fw-bold mb-0 fs-5">Delivery Info</h6>
                          </div>
                          <p
                            className="text-muted mb-0 lh-base"
                            style={{ whiteSpace: "pre-line" }}
                          >
                            {product.deliveryInfo}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* WhatsApp Inquiry Button - Full Width */}
                  <div className="mt-4">
                    <button
                      onClick={handleWhatsAppInquiry}
                      className="btn btn-lg w-100 d-flex align-items-center justify-content-center gap-2"
                      style={{
                        backgroundColor: "#25D366",
                        color: "white",
                        borderRadius: "5px",
                        padding: "12px 30px",
                        fontSize: "1.1rem",
                        fontWeight: "600",
                        border: "none",
                        boxShadow: "0 4px 15px rgba(37, 211, 102, 0.3)",
                        transition: "all 0.3s ease",
                      }}
                      onMouseOver={(e) =>
                        (e.currentTarget.style.backgroundColor = "#20BA5C")
                      }
                      onMouseOut={(e) =>
                        (e.currentTarget.style.backgroundColor = "#25D366")
                      }
                    >
                      <FaWhatsapp size={26} />
                      Inquire via WhatsApp
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="container text-center py-5">
              <h3>Product not found</h3>
              <Link to="/Product" className="btn btn-primary mt-3">
                Back to Products
              </Link>
            </div>
          )}
        </section>

        {/* More Products Section */}
        {!loading && product && (
          <section className="py-5 section-background">
            <div className="container">
              <div className="text-center mb-4">
                <h2 className="fw-semibold">
                  More <span className="text-orange">Products</span>
                </h2>
                <p className="text-muted">Explore our other NFC products</p>
              </div>

              {/* Skeleton Loading */}
              {moreProductsLoading ? (
                <div className="featureCategorySlider pt-1 feature-category-grid">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i} className="productCard rounded h-100">
                      <div className="card_product rounded">
                        <div
                          className="bg-light rounded-top w-100"
                          style={{ height: "220px" }}
                        />
                        <div className="border-yellow p-2 text-center">
                          <div className="placeholder-glow mb-2">
                            <span
                              className="placeholder col-8"
                              style={{ opacity: 0.1 }}
                            ></span>
                          </div>
                          <div className="placeholder-glow mb-2">
                            <span
                              className="placeholder col-5 me-2"
                              style={{ opacity: 0.1 }}
                            ></span>
                            <span
                              className="placeholder col-3"
                              style={{ opacity: 0.1 }}
                            ></span>
                          </div>
                          <div className="placeholder-glow">
                            <span
                              className="placeholder col-4"
                              style={{ opacity: 0.1 }}
                            ></span>
                          </div>
                        </div>
                        <div className="px-2 pb-2">
                          <div
                            className="bg-secondary rounded"
                            style={{ height: "38px", opacity: 0.1 }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="featureCategorySlider pt-1 feature-category-grid">
                  {allProducts
                    .filter((p) => p.id !== id)
                    .map((prod) => {
                      const imgs = prod.images || [prod.image];
                      const imgIndex = imageIndices[prod.id] || 0;
                      return (
                        <div
                          key={prod.id}
                          className="productCard rounded h-100"
                        >
                          <div
                            className="card_product rounded p-0"
                            style={{ cursor: "pointer" }}
                            onClick={() => navigate(`/product/${prod.id}`)}
                          >
                            <img
                              src={imgs[imgIndex]}
                              alt={prod.productName}
                              className="rounded-top w-100"
                              loading="lazy"
                            />
                            <div className="border-yellow p-2 text-center card-body">
                              <span className="fw-500">{prod.productName}</span>
                              <div>
                                <span className="text-danger text-decoration-line-through pe-1">
                                  ₹{prod.price}
                                </span>
                                <span className="text-success">
                                  ({prod.offerPrice}% OFF)
                                </span>
                              </div>
                              <div className="fw-bold">₹{prod.finalPrice}</div>
                            </div>
                            <div className="px-2 pb-2">
                              <button className="btn buy-now-btn rounded w-100">
                                VIEW PRODUCT
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </section>
        )}

        <Footer />
      </div>

      {/* Gallery Modal - Clean Dark UI */}
      <Modal
        show={showGalleryModal}
        onHide={() => setShowGalleryModal(false)}
        fullscreen
        dialogClassName="gallery-modal-full"
        contentClassName="gallery-modal-content"
        backdropClassName="gallery-modal-backdrop"
      >
        <Modal.Body
          className="p-0 m-0"
          style={{
            width: "100vw",
            height: "100vh",
            maxHeight: "100vh",
            backgroundColor: "#1a1a1a",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
          onClick={() => setShowGalleryModal(false)}
        >
          {/* Top Left - Title */}
          <div
            className="position-absolute"
            style={{ top: "20px", left: "25px", zIndex: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h5
              className="text-white mb-1 fw-bold"
              style={{ fontSize: "1.1rem" }}
            >
              {product?.productName}
            </h5>
          </div>

          {/* Top Right - Action Buttons */}
          <div
            className="position-absolute d-flex gap-2"
            style={{ top: "20px", right: "25px", zIndex: 20 }}
            onClick={(e) => e.stopPropagation()}
          >
            <a
              href={images[modalMainIdx]}
              download
              className="d-flex align-items-center justify-content-center"
              style={{
                width: "42px",
                height: "42px",
                borderRadius: "8px",
                backgroundColor: "#333",
                color: "white",
                textDecoration: "none",
                transition: "background-color 0.2s",
              }}
            >
              <FaDownload size={16} />
            </a>
            <button
              onClick={() => setShowGalleryModal(false)}
              className="d-flex align-items-center justify-content-center"
              style={{
                width: "42px",
                height: "42px",
                borderRadius: "8px",
                backgroundColor: "#333",
                color: "white",
                border: "none",
                cursor: "pointer",
              }}
            >
              <FaTimes size={18} />
            </button>
          </div>

          {/* Main Image View - Using ZoomableImage */}
          <div
            className="flex-grow-1 position-relative"
            style={{ overflow: "hidden" }}
          >
            {/* Left Navigation */}
            {images.length > 1 && (
              <button
                onClick={prevModalImage}
                className="gallery-nav-btn"
                style={{ left: "20px" }}
              >
                <FaChevronLeft size={15} />
              </button>
            )}

            <ZoomableImage src={images[modalMainIdx]} alt="Full View" />

            {/* Right Navigation */}
            {images.length > 1 && (
              <button
                onClick={nextModalImage}
                className="gallery-nav-btn"
                style={{ right: "20px" }}
              >
                <FaChevronRight size={15} />
              </button>
            )}
          </div>

          {/* Bottom Center - Thumbnail Grid */}
          {images.length > 1 && (
            <div
              className="position-absolute d-flex justify-content-center"
              style={{
                bottom: "25px",
                left: "50%",
                transform: "translateX(-50%)",
                zIndex: 20,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${Math.min(images.length, 6)}, 45px)`,
                  gap: "4px",
                  padding: "8px",
                  backgroundColor: "rgba(0, 0, 0, 0.1)",
                  borderRadius: "8px",
                  backdropFilter: "blur(10px)",
                }}
              >
                {images.map((src, i) => (
                  <div
                    key={i}
                    onClick={() => setModalMainIdx(i)}
                    style={{
                      width: "45px",
                      height: "45px",
                      borderRadius: "4px",
                      overflow: "hidden",
                      cursor: "pointer",
                      opacity: i === modalMainIdx ? 1 : 0.5,
                      border:
                        i === modalMainIdx
                          ? "2px solid white"
                          : "2px solid transparent",
                      transition: "all 0.2s",
                    }}
                  >
                    <img
                      src={src}
                      alt={`Thumb ${i + 1}`}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </Modal.Body>
      </Modal>
      <ToastContainer />
    </>
  );
};

export default ProductDetails;
